import React, { memo } from 'react';
import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Input from '@/shared/Input';
import { inputOnlyNumbers } from '@/utils/formValidation';
import Form from '@/shared/Form';
import { useForm } from 'react-hook-form';
import { otpSend } from '@/actions/ecom/login';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import useScreen from '@/hooks/useScreen';
import Paragraph from '@/shared/Paragraph';
import FixedWrapper from '@/shared/FixedWrapper';
import classNames from 'classnames';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import Cookies from '@/utils/cookie';
import { SESSION_COOKIE } from '@/configs/serviceConstants';
import dlv from 'dlv';
import is from '@/utils/is';
import useOnMount from '@/hooks/useOnMount';
import { useAppConfigContext } from '@/context/AppContext';
import { AMC_DEVICE_PAGE_URL } from '@/configs/constants';

interface LoginData {
  title?: string;
  content?: string;
  className?: string;
  btnClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  mobileBtnClassName?: string;
}

type LoginProps = {
  onLogin: Function;
  loginData?: LoginData;
  mesaage?: string;
};

type FormInputs = {
  mobileNumber: string;
};

type ButtonProps = {
  isLoading?: boolean;
  inputMobileNumber?: string;
  isMobile?: boolean;
  loginData?: LoginData;
};

const renderButton = ({ isLoading, inputMobileNumber, isMobile, loginData }: ButtonProps) => {
  return (
    <Button
      className={classNames('w-full rounded', {
        'mt-16': !isMobile,
        [loginData?.btnClassName || '']: !!loginData?.btnClassName,
      })}
      type="submit"
      disabled={Boolean(isLoading || !inputMobileNumber || inputMobileNumber.length != 10)}
      isLoading={isLoading}
    >
      Get OTP
    </Button>
  );
};

function Login({ onLogin, loginData }: LoginProps) {
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<FormInputs>();
  const { isMobile } = useScreen();

  const { state: appConfig } = useAppConfigContext();
  const inputMobileNumber = watch('mobileNumber');

  const { login } = appConfig;

  const fetch = (...args: any) => otpSend.apply(null, args);

  const { isLoading, promisifyTrigger, isError, error } = useTriggerRequest({ fetch });
  const { fireGTMEvent } = useGTM();
  const sessionHash = Cookies.get(SESSION_COOKIE);

  useOnMount(() => {
    if (!login.open && (sessionHash !== undefined || is.not.empty.string(sessionHash))) {
      window.location.assign(AMC_DEVICE_PAGE_URL);
      return;
    }
    fireGTMEvent(GTMEventNames.LOGIN_SCREEN_LANDED, {
      screen_name: SCREEN_NAMES.LOGIN_FORM,
      source_cta: '',
    });
  });

  const onSubmit = async (formData: FormInputs) => {
    const { mobileNumber } = formData;
    const loginData = { mobile_number: mobileNumber };
    const responseData = await promisifyTrigger(loginData);
    onLogin({ ...loginData, userId: dlv(responseData, 'data.data.userId') });

    fireGTMEvent(GTMEventNames.GET_OTP_CLICKED, {
      screen_name: SCREEN_NAMES.LOGIN_FORM,
      submission_error: !responseData?.success,
      error_message: !responseData?.success ? responseData?.message : '',
    });
  };
  if (isError) {
    errors.mobileNumber = {
      type: '',
      message: error.error,
    };
  }

  const handleInputFocus = () => {
    fireGTMEvent(GTMEventNames.MOBILE_NUMBER_FOCUSED, {
      screen_name: SCREEN_NAMES.LOGIN_FORM,
    });
  };

  return (
    <Div
      className={classNames(
        'mt-8 md:p-10 md:bg-white md:rounded-xl  md:border md:border-core-outline-subtle',
        loginData?.className,
      )}
    >
      <Div className="flex flex-col gap-6 ">
        <Paragraph
          className={classNames(
            'text-h-1 md:text-hd-1 text-cti-primary',
            loginData?.titleClassName,
          )}
        >
          {login.message ?? loginData?.title}
        </Paragraph>

        <Form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
          <Input
            type="tel"
            placeholder="Enter mobile number"
            maxLength={10}
            {...register('mobileNumber', {
              pattern: {
                value: /^[0-9]{10}$/,
                message: 'Invalid mobile number', // JS only: <p>error message</p> TS only support string
              },
            })}
            onKeyPress={inputOnlyNumbers}
            onFocus={handleInputFocus}
            data-testid="loginForm"
            errors={errors}
          />

          <Paragraph className={classNames('text-black/50 mt-1', loginData?.contentClassName)}>
            {loginData?.content}
          </Paragraph>
          {isMobile ? (
            <FixedWrapper
              className={classNames('md:hidden p-6 pt-4', loginData?.mobileBtnClassName)}
            >
              {renderButton({ isLoading, inputMobileNumber, isMobile: true, loginData })}
            </FixedWrapper>
          ) : (
            renderButton({ isLoading, inputMobileNumber, isMobile: false, loginData })
          )}
        </Form>
      </Div>
    </Div>
  );
}

export default memo(Login);
